import { NextCustomImage } from 'components/Image';
import { CheckmarkLogo } from 'icons/CheckmarkLogo';
import getCommonTestIds from 'constants/testsIds/common';
import type { Author } from 'interfaces/content/Authors';
import type { Reviewer } from 'interfaces/content/AuthorByline';
import type { EventData } from 'services/Gtm/entities';
import { useCallback, useState } from 'react';
import * as S from './ContributorStyles';

interface ContributorImageProps {
  image: string | null;
  contributorName: string;
}

export interface ContributorProps {
  contributor: Author | Reviewer;
  type: 'author' | 'reviewer';
  trackingData?: EventData;
}

interface ContributorInformationProps extends ContributorProps {
  showDetails?: boolean;
}

export const ContributorImage = ({ image, contributorName }: ContributorImageProps) =>
  image && (
    <NextCustomImage
      src={image}
      alt={contributorName}
      width='40'
      height='40'
      style={{ borderRadius: '30px', maxWidth: 'unset' }}
    />
  );

export const ContributorName = ({
  contributor,
  type,
  trackingData,
  showDetails = false,
}: ContributorInformationProps) => {
  const isReviewer = type === 'reviewer';

  const infoText = {
    author: showDetails ? 'By ' : 'Written by ',
    reviewer: 'Reviewed by ',
  };

  return (
    <>
      <S.InfoText data-cy={getCommonTestIds(isReviewer ? 'REVIEWED_BY_TEXT' : 'AUTHOR_BY_TEXT')}>
        {infoText[type]}
      </S.InfoText>
      <S.Name
        ariaLabel={`Go to ${contributor.name} author's page`}
        type='primary'
        href={contributor.uri}
        data-cy={getCommonTestIds(isReviewer ? 'REVIEWER_NAME' : 'AUTHOR_NAME')}
        trackingData={
          trackingData && {
            ...trackingData,
            eventLabel: `${contributor.name} : ${trackingData.eventLabel}`,
          }
        }
      >
        {contributor.name}
      </S.Name>
    </>
  );
};

export const Contributor = ({ contributor, type, trackingData }: ContributorProps) => {
  const isReviewer = type === 'reviewer';
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const toggleTooltip = useCallback(
    (state: boolean) => {
      setTooltipOpen(state);
    },
    [setTooltipOpen],
  );

  return (
    <S.ContributorWrapper
      onMouseEnter={() => toggleTooltip(true)}
      onMouseLeave={() => toggleTooltip(false)}
    >
      <S.ContributorDetails>
        <ContributorImage
          image={contributor.image}
          contributorName={contributor.name}
        />
        <S.ContributorInformation>
          <span>
            {isReviewer && <CheckmarkLogo />}
            <ContributorName
              contributor={contributor}
              type={type}
              showDetails
              trackingData={trackingData}
            />
          </span>
          {contributor.role && <S.Role>{contributor.role}</S.Role>}
        </S.ContributorInformation>
      </S.ContributorDetails>
      {contributor.shortIntro && (
        <S.Tooltip
          className='tooltip-author'
          $tooltipOpen={tooltipOpen}
          toggleTooltip={toggleTooltip}
          author={contributor as Author}
        />
      )}
    </S.ContributorWrapper>
  );
};
